import React, { useState, useEffect } from 'react'

interface FlowLogoProps {
  className?: string
  animate?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export const FlowLogo: React.FC<FlowLogoProps> = ({ 
  className = '', 
  animate = true,
  size = 'md' 
}) => {
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (animate) {
      // Start animation on mount
      setIsAnimating(true)
      
      // Stop animation after 2 spins (6 seconds at 3s per spin)
      const timer = setTimeout(() => {
        setIsAnimating(false)
      }, 6000)
      
      return () => clearTimeout(timer)
    }
  }, [animate])

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  }

  return (
    <div className={`${sizeClasses[size]} ${className} relative flex items-center justify-center`}>
      <svg 
        viewBox="0 0 100 100" 
        className={`w-full h-full transition-transform duration-300 ${isAnimating ? 'animate-spin-slow' : ''}`}
        style={{ transformOrigin: 'center' }}
      >
        {/* Circular segments - Reebelo-style spinner */}
        {[...Array(10)].map((_, i) => {
          const angle = (i * 36) - 90 // 360/10 = 36 degrees per segment, start at top
          const startAngle = angle * (Math.PI / 180)
          const endAngle = (angle + 25) * (Math.PI / 180) // 25 degree arc
          
          const innerRadius = 25
          const outerRadius = 45
          
          // Calculate path coordinates
          const x1 = 50 + innerRadius * Math.cos(startAngle)
          const y1 = 50 + innerRadius * Math.sin(startAngle)
          const x2 = 50 + outerRadius * Math.cos(startAngle)
          const y2 = 50 + outerRadius * Math.sin(startAngle)
          const x3 = 50 + outerRadius * Math.cos(endAngle)
          const y3 = 50 + outerRadius * Math.sin(endAngle)
          const x4 = 50 + innerRadius * Math.cos(endAngle)
          const y4 = 50 + innerRadius * Math.sin(endAngle)
          
          return (
            <path
              key={i}
              d={`M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius} ${outerRadius} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 0 0 ${x1} ${y1}`}
              fill="#1d4ed8"
              className="dark:fill-blue-500"
            />
          )
        })}
        
        {/* Center circle */}
        <circle 
          cx="50" 
          cy="50" 
          r="20" 
          fill="#3b82f6"
          className="dark:fill-blue-600"
        />
      </svg>
    </div>
  )
}

// Loading spinner variant with more prominent animation
export const FlowLogoLoading: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-4">
      <div className={`relative ${className}`}>
        <FlowLogo size="lg" animate={true} />
        <div className="absolute inset-0 rounded-lg border-4 border-blue-400 border-t-transparent animate-spin" />
      </div>
      <div className="text-sm font-medium text-gray-600 dark:text-gray-400 animate-pulse">
        Loading...
      </div>
    </div>
  )
}

